/**
 * Return on investment.
 *
 * Convention: total cost = amount invested + fees and other costs; total return = value
 * at the end + income received (dividends, rent, interest) along the way; net profit =
 * total return − total cost; ROI = net profit ÷ total cost. Annualized ROI is the
 * constant yearly rate that gives the same growth, (total return ÷ total cost)^(1/years) − 1.
 * It assumes the income was received and kept, not reinvested at a particular rate; for
 * dated, uneven cash flows use an IRR instead.
 */

export interface RoiInput {
  invested: number;
  finalValue: number;
  income: number;
  costs: number;
  years: number;
}

export interface RoiResult {
  totalCost: number;
  totalReturn: number;
  netProfit: number;
  roi: number;
  /** Null when the holding period is not above zero. */
  annualized: number | null;
}

export function computeRoi(input: RoiInput): RoiResult | null {
  const totalCost = input.invested + input.costs;
  if (!(totalCost > 0)) {
    return null;
  }
  const totalReturn = input.finalValue + input.income;
  const netProfit = totalReturn - totalCost;
  const growth = totalReturn / totalCost;
  const annualized =
    input.years > 0 && growth >= 0 ? growth ** (1 / input.years) - 1 : null;
  return {
    totalCost,
    totalReturn,
    netProfit,
    roi: netProfit / totalCost,
    annualized,
  };
}

/**
 * Positions (1 = best) by annualized ROI where every investment has one, otherwise by
 * plain ROI. Ties share a position.
 */
export function rankRois(results: readonly RoiResult[]): number[] {
  const useAnnual = results.every((r) => r.annualized !== null);
  const score = (r: RoiResult) => (useAnnual ? (r.annualized ?? 0) : r.roi);
  return results.map(
    (r) => 1 + results.filter((other) => score(other) > score(r) + 1e-12).length
  );
}
